import { Box, Typography, useMediaQuery, useTheme } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import ContainerLayout from "../../../../components/container-layout/ContainerLayout";
import { ButtonPrimary } from "../../../../components/button/ButtonCustoms";
import ModalCustom from "../../../../components/modal/ModalCustom";
import NavigateListCustom from "../../../../components/navigation/NavigationListCustom";
import { routeName } from "../../../../routes/routeName";
import AccordionUpload from "../../../../components/accordion/AccordionUpload";
import ImageSlider from "../../../../components/slider/slider-images";
import { NavigateFunction, useLocation, useNavigate, useParams } from "react-router-dom";
import IndexProjectForm from "../components/IndexProjectForm";
import ContainerButton from "../../../../components/button/ContainerButton";
import InfoIcon from "@mui/icons-material/Info";
import {
  UploadedFile,
  InputValueProjectForm,
  defualtValueProjectForm,
  ProjectSubmissionData,
} from "../components/domain";
import {
  setLoading,
  unsetLoading,
} from "../../../../store/loading/loading.slice";
import { alertError, alertSuccess } from "../../../../components/alert/swal";
import { edpexProjectAPI } from "../../../../api/edpex-project/edpex-project.api";
import { edpexFormMasterAPI } from "../../../../api/edpex-form-master/edpex-form-master.api";
import { RootState } from "../../../../store/store";

const FormAddProject = () => {
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.up("md"));
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate: NavigateFunction = useNavigate();
  const { id } = useParams();

  const base_url = useSelector((state: RootState) => state?.api.image_api);

  const location = useLocation();
  const pathname = location.pathname;

  // Determine if we're in edit mode
  const isEditMode = Boolean(id);

  const [showModal, setShowModal] = useState<boolean>(false);

  const [inputValue, setInputValue] = useState<InputValueProjectForm>(
    defualtValueProjectForm
  );
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [projectStatusCheck, setProjectStatusCheck] = useState<string>("");

  const [uploadedFilesSlide, setUploadedFilesSlide] = useState<UploadedFile[]>(
    []
  );
  const [multiUpload, setMultiImage] = useState<any[]>([]);

  const handleGetDataPopUp = useCallback(async () => {
    try {
      dispatch(setLoading());

      const res = await edpexFormMasterAPI.getEdpexFormMasterByType({
        form_type: "EXAMPLE_PROJECT",
        is_release: 1,
      });

      if (res.data?.status < 300 && res.data?.data) {
        const {
          edpex_form_name,
          document_files = [],
          image_files = [],
        } = res.data.data;
        // const baseURL = "https://ibscapi.kivaru.com/";

        const mapFileData = (
          files: any[],
          type: "FILE_IMAGE" | "FILE_DOCUMENT"
        ): UploadedFile[] =>
          files.map((file) => ({
            id: file.id_edpex_form_file,
            name: file.file_name,
            size: file.file_size
              ? `${(file.file_size / 1024).toFixed(2)} KB`
              : "Unknown",
            uploadDate: file.modified_date || file.created_date || "N/A",
            file_path: file.file_path,
            fullUrl: `${base_url}${file.file_path}${encodeURIComponent(
              file.file_name
            )}`,
            type,
            file: null as unknown as File, // Placeholder for UI compatibility
          }));

        setUploadedFilesSlide([
          ...mapFileData(document_files, "FILE_DOCUMENT"),
        ]);
        setMultiImage([...mapFileData(image_files, "FILE_IMAGE")]);
      }
    } catch (error: any) {
      console.error("Get data error:", error);
      alertError(t("เกิดข้อผิดพลาด"), t("โหลดข้อมูลไม่สำเร็จ"));
    } finally {
      dispatch(unsetLoading());
    }
  }, [dispatch, t]);

  useEffect(() => {
    handleGetDataPopUp();
  }, [handleGetDataPopUp]);

  useEffect(() => {
    if (isEditMode && id) {
      loadProjectData(parseInt(id));
    }
  }, [id, isEditMode]);

  function convertFromApiFormat(apiData: any) {
    const formData: any = {
      edpex_project_indicators: [],
      edpex_project_budgets: [],
      edpex_project_responsible: [],
    };

    // Convert indicators back
    if (apiData.edpex_project_indicators) {
      formData.edpex_project_indicators = apiData.edpex_project_indicators.map(
        (indicator: any) => ({
          id: indicator.id_edpex_project_indicators
            ? indicator.id_edpex_project_indicators.toString()
            : "",
          name: indicator.edpex_project_indicators || "",
          type: indicator.edpex_project_indicators_target || "LEVEL",
          value: indicator.edpex_project_indicators_target_point || "",
          value_2: indicator.edpex_project_indicators_point || "",
        })
      );
    }

    // Convert budgets back
    if (apiData.edpex_project_budgets) {
      formData.edpex_project_budgets = apiData.edpex_project_budgets.map(
        (budget: any) => ({
          spending_info_id: budget.id_edpex_project_budget
            ? budget.id_edpex_project_budget.toString()
            : "",
          spending_info_month: budget.fiscal_month || "",
          spending_info_year: budget.fiscal_year || "",
          spending_info_value: budget.edpex_project_budget_amount
            ? budget.edpex_project_budget_amount.toString()
            : "0",
        })
      );
    }

    // Convert responsible persons back
    if (apiData.edpex_project_responsible) {
      formData.edpex_project_responsible =
        apiData.edpex_project_responsible.map((person: any) => ({
          id_employee: person.id_edpex_responsible || 0,
          name: person.name,
          employee_no: person.employee_no,
          status: person.edpex_project_responsible_type || "PRIMARY",
        }));
    }

    return formData;
  }

  const loadProjectData = useCallback(
    async (projectId: number) => {
      try {
        setIsLoading(true);
        dispatch(setLoading());

        const response = await edpexProjectAPI.getEdpexProjectById(projectId);

        if (response.data.success && response.data.data) {
          const projectData = response.data.data;

          const formData = convertFromApiFormat(projectData);

          setProjectStatusCheck(projectData.edpex_project_status);
          // Map API data to form structure
          setInputValue({
            project_name: projectData.edpex_project_name || "",
            fiscal_year: +projectData.fiscal_year + 543,
            start_date: projectData.edpex_project_start_date || "",
            end_date: projectData.edpex_project_end_date || "",
            budget: projectData.edpex_project_budget || 0,
            project_type: projectData.edpex_project_status || "",
            edpex_project_ongoing: projectData.edpex_project_ongoing,
            edpex_project_strategic_work:
              projectData.edpex_project_strategic_work,
            id_project_continue: projectData.id_edpex_project_ref || 0,

            responsible_department:
              projectData.edpex_project_department_responsible || "",
            responsible_director:
              projectData.edpex_project_user_responsible || "",
            responsible_work:
              projectData.edpex_project_division_responsible || "",

            edpex_mission: projectData.id_edpex_mission?.toString() || "",
            edpex_criteria: projectData.id_edpex_standard_point || 0,
            edpex_criteria_sub: projectData.id_edpex_standard_point_detail || 0,
            objective: projectData.edpex_project_objective || "",
            target_group: projectData.edpex_project_target_group || "",
            edpex_result: projectData.edpex_project_result || "",
            // indicatorList: projectData.edpex_project_indicators || [],
            // spendingInfo: projectData.edpex_project_budgets || [],
            // responsiblePersons: projectData.edpex_project_responsible || [],
            indicatorList: formData.edpex_project_indicators,
            spendingInfo: formData.edpex_project_budgets,
            responsiblePersons: formData.edpex_project_responsible,
            //
            description: projectData.edpex_project_remark || "",
            project_status: projectData.edpex_project_status || "",
          });

          // Load existing files with improved mapping
          if (
            projectData.edpex_project_files &&
            Array.isArray(projectData.edpex_project_files)
          ) {
            setUploadedFiles(
              projectData.edpex_project_files.map((file: any) => ({
                id: file.id || file?.id_edpex_project_file,
                name: file.original_name || file?.file_name || file.name,
                size: file.file_size || 0,
                type: file.mime_type || file.type || "",
                file_path: file.file_path || file.url,
              }))
            );
          }
        } else {
          throw new Error(response.message || t("error.loadProjectFailed"));
        }
      } catch (error: any) {
        console.error("Error loading project data:", error);
        alertError(error.message || t("error.loadProjectFailed"));
      } finally {
        setIsLoading(false);
        dispatch(unsetLoading());
      }
    },
    [dispatch, t]
  );

  const handleFileUpload = async (files: File[]): Promise<number[]> => {
    if (files.length === 0) return [];

    try {
      const formData = new FormData();
      files.forEach((file: File) => {
        formData.append("files", file);
      });

      const res = await edpexProjectAPI.uploadEdpexProjectFile(formData);

      // Extract file IDs from the correct path: res.data.data
      const fileIds =
        res.data?.data?.map((file: any) => file.id_edpex_project_file) || [];
      return fileIds;
    } catch (error: any) {
      console.error("File upload error:", error);
      alertError(
        t("เกิดข้อผิดพลาด"),
        error?.response?.data?.message || t("อัปโหลดไฟล์ไม่สำเร็จ")
      );
      return [];
    }
  };

  function convertToApiFormat(formData: any) {
    const apiData = {
      edpex_project_indicators: [],
      edpex_project_budgets: [],
      edpex_project_responsible: [],
    };

    // Convert indicators
    if (formData.edpex_project_indicators) {
      apiData.edpex_project_indicators = formData.edpex_project_indicators.map(
        (indicator: any, index: number) => ({
          id_edpex_project_indicators: indicator.id || null,
          seq: index,
          edpex_project_indicators: indicator.name || "",
          edpex_project_indicators_target: indicator.type || "",
          edpex_project_indicators_target_point: indicator.value || "",
          edpex_project_indicators_point: indicator.value_2 || "",
        })
      );
    }

    // Convert budgets
    if (formData.edpex_project_budgets) {
      apiData.edpex_project_budgets = formData.edpex_project_budgets.map(
        (budget: any) => ({
          id_edpex_project_budget: budget.spending_info_id || null,
          fiscal_year: budget.spending_info_year || "",
          fiscal_month: budget.spending_info_month || "",
          edpex_project_budget_amount:
            parseFloat(budget.spending_info_value) || 0,
        })
      );
    }

    // Convert responsible persons
    if (formData.edpex_project_responsible) {
      apiData.edpex_project_responsible =
        formData.edpex_project_responsible.map((person: any) => ({
          id_edpex_project_responsible: person.id || null,
          edpex_project_responsible_type: person.status || "PRIMARY",
          id_edpex_responsible: person.id_employee || 0,
        }));
    }

    return apiData;
  }

  // Build project form data
  const buildProjectSubmissionData = useCallback(
    async (status: boolean): Promise<ProjectSubmissionData> => {
      try {
        const newFileIds = await handleFileUpload(
          uploadedFiles.map((file) => file.file).filter(Boolean)
        );

        const allFileIds = [
          ...uploadedFiles.map((file) => file.id).filter((id) => id),
          ...newFileIds,
        ];

        const formBodyData = {
          edpex_project_indicators: inputValue.indicatorList,
          edpex_project_budgets: inputValue.spendingInfo,
          edpex_project_responsible: inputValue.responsiblePersons,
        };

        const apiFormatData = convertToApiFormat(formBodyData);

        return {
          edpex_project_name: inputValue.project_name,
          fiscal_year: inputValue.fiscal_year - 543,
          edpex_project_start_date: inputValue.start_date,
          edpex_project_end_date: inputValue.end_date,
          edpex_project_budget: inputValue.budget,
          edpex_project_strategic_work: inputValue.edpex_project_strategic_work,
          edpex_project_ongoing: inputValue.edpex_project_ongoing,
          id_edpex_project_ref: inputValue.id_project_continue,
          //

          edpex_project_division_responsible: inputValue.responsible_work,
          edpex_project_department_responsible:
            inputValue.responsible_department,
          edpex_project_user_responsible: inputValue.responsible_director,
          //
          id_edpex_mission: parseInt(inputValue.edpex_mission.toString()),
          id_edpex_standard_point: inputValue.edpex_criteria,
          id_edpex_standard_point_detail: inputValue.edpex_criteria_sub,
          edpex_project_objective: inputValue.objective,
          edpex_project_target_group: inputValue.target_group,
          edpex_project_result: inputValue.edpex_result,
          // edpex_project_status: inputValue.project_status,
          edpex_project_status: status,
          edpex_project_remark: inputValue.description,
          edpex_project_indicators: apiFormatData.edpex_project_indicators,
          edpex_project_budgets: apiFormatData.edpex_project_budgets,
          edpex_project_responsible: apiFormatData.edpex_project_responsible,
          id_edpex_project_files: allFileIds,
        };
      } catch (error) {
        console.error("Error building submission data:", error);
        throw error; // rethrow to let calling code handle it
      }
    },
    [inputValue, uploadedFiles, isEditMode]
  );

  const validateForm = useCallback((): boolean => {
    const errors: string[] = [];

    if (!inputValue.project_name?.trim()) {
      errors.push(t("ระบุชื่อโครงการ"));
      // errors.push(t("validation.projectNameRequired"));
    }

    if (!inputValue.fiscal_year || inputValue.fiscal_year < 1900) {
      errors.push(t("ระบุปีงบประมาณหรือปีงบประมาณไม่ถูกต้อง"));
      // errors.push(t("validation.fiscalYearRequired"));
    }

    if (!inputValue.start_date) {
      errors.push(t("ระบุวันที่เริ่มต้น"));
      // errors.push(t("validation.startDateRequired"));
    }

    if (!inputValue.end_date) {
      errors.push(t("ระบุวันที่สิ้นสุด"));
      // errors.push(t("validation.endDateRequired"));
    }

    if (inputValue.start_date && inputValue.end_date) {
      if (new Date(inputValue.start_date) >= new Date(inputValue.end_date)) {
        errors.push(t("วันที่เริ่มต้นไม่ควรมากกว่าวันที่สิ้นสุด"));
        // errors.push(t("validation.invalidDateRange"));
      }
    }

    if (!inputValue.budget || inputValue.budget <= 0) {
      // errors.push(t("validation.budgetRequired"));
      errors.push(t("ระบุงบประมาณ"));
    }

    if (!inputValue.objective?.trim()) {
      errors.push(t("ระบุวัตถุประสงค์"));
    }

    if (errors.length > 0) {
      alertError(errors.join("\n"));
      return false;
    }

    return true;
  }, [inputValue, t]);

  // Create project
  const handleCreateProject = useCallback(
    async (data: ProjectSubmissionData) => {
      const response = await edpexProjectAPI.createEdpexProject(data);

      if (response.data.success) {
        alertSuccess(
          t("เพี่มโครงการ/กิจกรรม/งาน"),
          t("เพี่มโครงการ/กิจกรรม/งาน เรียบร้อยแล้ว"),
          undefined,
          () => {
            navigate(routeName.cms.edpexAddProject);
          }
        );
      } else {
        alertError(t("เพี่มโครงการ/กิจกรรม/งาน ไม่สำเร็จ"));
      }
    },
    [navigate, t]
  );

  // Update project
  const handleUpdateProject = useCallback(
    async (data: ProjectSubmissionData) => {
      if (!id) throw new Error("Project ID is required for update");

      const response = await edpexProjectAPI.updateEdpeProject(
        parseInt(id),
        data
      );

      if (response.data.success) {
        alertSuccess(
          t("แก้ไขโครงการ/กิจกรรม/งาน"),
          t("แก้ไขโครงการ/กิจกรรม/งาน เรียบร้อยแล้ว"),
          undefined,
          () => {
            loadProjectData(parseInt(id));
          }
        );
      } else {
        alertError(t("แก้ไขโครงการ/กิจกรรม/งาน ไม่สำเร็จ"));
      }
    },
    [id, navigate, t]
  );

  const handleSubmit = useCallback(
    async (status: boolean) => {
      if (isSubmitting) return;

      try {
        setIsSubmitting(true);
        dispatch(setLoading());

        // Validate form
        if (!validateForm()) {
          return;
        }

        // Build submission data
        const submissionData = await buildProjectSubmissionData(status);

        // Submit based on mode
        if (isEditMode) {
          await handleUpdateProject(submissionData);
        } else {
          await handleCreateProject(submissionData);
        }
      } catch (error: any) {
        alertError(t("เกิดข้อผิดพลาด"));
      } finally {
        setIsSubmitting(false);
        dispatch(unsetLoading());
      }
    },
    [
      isSubmitting,
      dispatch,
      validateForm,
      buildProjectSubmissionData,
      isEditMode,
      handleUpdateProject,
      handleCreateProject,
      t,
    ]
  );

  return (
    <>
      <ContainerLayout
        title={t("เพิ่มโครงการ/กิจกรรม/งาน ใหม่")}
        hidden_sub_title
        sub_title_custom={
          <>
            <Box
              my={1}
              sx={{ display: "flex", alignItems: "center", gap: 1 }}
              onClick={() => {
                setShowModal(true);
              }}
            >
              <InfoIcon color="disabled" />
              <Typography>{t("ข้อมูล")}</Typography>
            </Box>
          </>
        }
        navigateContent={
          <NavigateListCustom
            navigateData={[
              {
                key: "1",
                name: t("ระบบ Edpex"),
                disabled_match: true,
                pathName: routeName.cms.edpexResultDisplay,
              },
              {
                key: "2",
                name: t("เพิ่มโครงการ/กิจกรรม/งาน"),
                pathName: routeName.cms.edpexAddProject,
              },
              {
                key: "3",
                name: t("เพิ่มโครงการ/กิจกรรม/งาน ใหม่"),
                pathName: pathname
              },
            ]}
          />
        }
        buttonContent={
          <ContainerButton
            justifyContent="end"
            buttonData={
              id && projectStatusCheck === "DRAFT"
                ? [
                  {
                    buttonProps: {
                      onClick: () => {
                        navigate(-1);
                      },
                    },
                    key: "CANCEL",
                    label: t("ยกเลิก"),
                    seq: 1,
                    type: "CANCEL",
                  },
                  {
                    buttonProps: {
                      onClick: () => {
                        handleSubmit(false);
                      },
                    },
                    key: "SAVE_DRAFT",
                    label: t("บันทึกร่าง", { context: "action" }),
                    seq: 2,
                    type: "SAVE_DRAFT",
                  },
                  {
                    buttonProps: {
                      onClick: () => {
                        handleSubmit(true);
                      },
                    },
                    key: "ADD2",
                    label: t("ยืนยัน"),
                    seq: 3,
                    type: "ADD2",
                  },
                ]
                : id && projectStatusCheck !== "DRAFT"
                  ? [
                    {
                      buttonProps: {
                        onClick: () => {
                          navigate(-1);
                        },
                      },
                      key: "CANCEL",
                      label: t("ยกเลิก"),
                      seq: 1,
                      type: "CANCEL",
                    },
                  ]
                  : [
                    {
                      buttonProps: {
                        onClick: () => {
                          navigate(-1);
                        },
                      },
                      key: "CANCEL",
                      label: t("ยกเลิก"),
                      seq: 1,
                      type: "CANCEL",
                    },
                    {
                      buttonProps: {
                        onClick: () => {
                          handleSubmit(false);
                        },
                      },
                      key: "SAVE_DRAFT",
                      label: t("บันทึกร่าง", { context: "action" }),
                      seq: 2,
                      type: "SAVE_DRAFT",
                    },
                    {
                      buttonProps: {
                        onClick: () => {
                          handleSubmit(true);
                        },
                      },
                      key: "ADD2",
                      label: t("ยืนยัน"),
                      seq: 3,
                      type: "ADD2",
                    },
                  ]
            }
          />
        }
        content={
          <>
            <IndexProjectForm
              inputValue={inputValue}
              setInputValue={setInputValue}
              uploadedFiles={uploadedFiles}
              setUploadedFiles={setUploadedFiles}
              disable={!!id && projectStatusCheck !== "DRAFT"}
            />
          </>
        }
      />

      {showModal && (
        <ModalCustom
          open
          title={t("เพิ่มโครงการ/กิจกรรม/งาน ใหม่")}
          subTitle={t("")}
          onClose={() => {
            setShowModal(false);
          }}
          component={
            <div className="w-full">
              <ImageSlider slides={multiUpload} />

              <AccordionUpload
                defaultExpanded
                required
                enableButtonLeft
                titleName={t("ไฟล์แนบ")}
                uploadedFiles={uploadedFilesSlide}
                setUploadedFiles={setUploadedFilesSlide}
                acceptedFileTypes=".pdf,.doc,.docx,.jpg,.png,.xlsx"
                maxFileSize={5}
                multiple={true}
                isView={true}
              />

              <div className="mt-6 w-full flex justify-center gap-4 items-center">
                <span>
                  <ButtonPrimary
                    mode="cancel"
                    buttonName={t("BUTTON.CANCEL")}
                    onClick={() => {
                      setShowModal(false);
                    }}
                  />
                </span>
              </div>
            </div>
          }
        />
      )}

      <br />

      <ContainerButton
        justifyContent="center"
        buttonData={
          id && projectStatusCheck === "DRAFT"
            ? [
              {
                buttonProps: {
                  onClick: () => {
                    navigate(-1);
                  },
                },
                key: "CANCEL",
                label: t("ยกเลิก"),
                seq: 1,
                type: "CANCEL",
              },
              {
                buttonProps: {
                  onClick: () => {
                    handleSubmit(false);
                  },
                },
                key: "SAVE_DRAFT",
                label: t("บันทึกร่าง", { context: "action" }),
                seq: 2,
                type: "SAVE_DRAFT",
              },
              {
                buttonProps: {
                  onClick: () => {
                    handleSubmit(true);
                  },
                },
                key: "ADD2",
                label: t("ยืนยัน"),
                seq: 3,
                type: "ADD2",
              },
            ]
            : id && projectStatusCheck !== "DRAFT"
              ? [
                {
                  buttonProps: {
                    onClick: () => {
                      navigate(-1);
                    },
                  },
                  key: "CANCEL",
                  label: t("ยกเลิก"),
                  seq: 1,
                  type: "CANCEL",
                },
              ]
              : [
                {
                  buttonProps: {
                    onClick: () => {
                      navigate(-1);
                    },
                  },
                  key: "CANCEL",
                  label: t("ยกเลิก"),
                  seq: 1,
                  type: "CANCEL",
                },
                {
                  buttonProps: {
                    onClick: () => {
                      handleSubmit(false);
                    },
                  },
                  key: "SAVE_DRAFT",
                  label: t("บันทึกร่าง", { context: "action" }),
                  seq: 2,
                  type: "SAVE_DRAFT",
                },
                {
                  buttonProps: {
                    onClick: () => {
                      handleSubmit(true);
                    },
                  },
                  key: "ADD2",
                  label: t("ยืนยัน"),
                  seq: 3,
                  type: "ADD2",
                },
              ]
        }
      />
    </>
  );
};

export default FormAddProject;
